import React from 'react';
import {
    Box,
    FormControl,
    InputAdornment,
    ListSubheader,
    MenuItem,
    Select,
    TextField,
    Tooltip,
} from '@mui/material';
import { SearchIcon } from 'assets';
import { COLORS, TooltipContent } from 'components';
import type { AllowedValuesDTO } from 'api/allowed-values';
import { useEntitySelect } from 'pages/authenticated/accelerate/targets/components/add-target-modal/hooks';
import { entitySelectSx } from 'pages/authenticated/accelerate/targets/components/add-target-modal/styles';
import type { InitiativeLevelType } from 'api/initiatives/types';
import { FieldLabel } from '../../field-label';

function toTargetLevel(level: InitiativeLevelType): 'Site' | 'Business Unit' | 'Corporate' {
    if (level === 'SITE') return 'Site';
    if (level === 'BUSINESS_UNIT') return 'Business Unit';
    return 'Corporate';
}

export interface LevelEntityFieldProps {
    level: InitiativeLevelType;
    value: string;
    onChange: (value: string) => void;
    allowedValues: AllowedValuesDTO | undefined;
    error?: string;
    required?: boolean;
    tooltip?: string | TooltipContent;
}

export const LevelEntityField: React.FC<LevelEntityFieldProps> = ({
    level,
    value,
    onChange,
    allowedValues,
    error,
    required,
    tooltip,
}) => {
    const targetLevel = toTargetLevel(level);
    const entityState = useEntitySelect(targetLevel, allowedValues);
    const {
        displayedOptions,
        hasMoreItems,
        remainingCount,
        searchText,
        handleSearchChange,
        handleLoadMore,
        handleMenuClose,
        handleMenuOpen,
        selectRef,
        menuOpen,
        selectWidth,
    } = entityState;

    const label = level === 'BUSINESS_UNIT' ? 'Business Unit' : 'Site';
    const isDisabled = level === 'GLOBAL';

    if (isDisabled) {
        return (
            <Box>
                <FieldLabel>Entity</FieldLabel>
                <Select
                    disabled
                    displayEmpty
                    value=""
                    sx={{ ...entitySelectSx, width: '100%' }}
                    renderValue={() => 'N/A — applies globally'}
                />
            </Box>
        );
    }

    return (
        <Box>
            <FieldLabel required={required} tooltip={tooltip}>
                {label}
            </FieldLabel>
            <FormControl ref={selectRef} fullWidth>
                <Select
                    sx={{ ...entitySelectSx, borderColor: error ? 'error.main' : undefined }}
                    open={menuOpen}
                    onOpen={handleMenuOpen}
                    onClose={handleMenuClose}
                    MenuProps={{
                        autoFocus: false,
                        PaperProps: {
                            sx: {
                                maxHeight: 400,
                                borderRadius: '8px',
                                ...(selectWidth != null && { width: selectWidth, minWidth: selectWidth }),
                            },
                        },
                        MenuListProps: { sx: { maxWidth: '100%', paddingTop: 0 } },
                    }}
                    displayEmpty
                    value={value || ''}
                    renderValue={() => value || `Select ${label.toLowerCase()}...`}
                >
                    <ListSubheader
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            backgroundColor: COLORS.white,
                            paddingTop: 1,
                            paddingBottom: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <TextField
                            size="small"
                            autoFocus
                            placeholder={`Search for a ${label.toLowerCase()}`}
                            fullWidth
                            value={searchText}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            onKeyDown={(e) => { if (e.key !== 'Escape') e.stopPropagation(); }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </ListSubheader>

                    {displayedOptions.length > 0 ? (
                        <>
                            {displayedOptions.map((opt) => (
                                <MenuItem
                                    key={opt.value}
                                    value={opt.value}
                                    onClick={() => { onChange(opt.value); handleMenuClose(); }}
                                    sx={{
                                        color: COLORS.Mainblue,
                                        fontWeight: 500,
                                        maxWidth: '100%',
                                        '&:hover': { backgroundColor: COLORS.White_Lilac },
                                        '&.Mui-selected': { backgroundColor: COLORS.White_Lilac },
                                        '&.Mui-selected:hover': { backgroundColor: COLORS.White_Lilac },
                                    }}
                                >
                                    <Tooltip title={opt.label} placement="top" enterDelay={400}>
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: '100%',
                                            }}
                                        >
                                            {opt.label}
                                        </Box>
                                    </Tooltip>
                                </MenuItem>
                            ))}
                            {hasMoreItems && (
                                <Box
                                    component="div"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleLoadMore(); }}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'left',
                                        alignItems: 'center',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        color: COLORS.Mainblue,
                                        '&:hover': { backgroundColor: COLORS.White_Lilac },
                                    }}
                                >
                                    Load more ({remainingCount} remaining)
                                </Box>
                            )}
                        </>
                    ) : (
                        <MenuItem disabled>
                            {searchText ? `No ${label.toLowerCase()}s found` : `No ${label.toLowerCase()}s available`}
                        </MenuItem>
                    )}
                </Select>
                {error && (
                    <Box sx={{ fontSize: '12px', color: 'error.main', mt: '4px' }}>{error}</Box>
                )}
            </FormControl>
        </Box>
    );
};
